import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, Phone } from 'lucide-react';

const api = axios.create({ baseURL: (process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL), withCredentials: true });

function initials(name) {
  return (name || '').split(' ').map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
}

/* Promo + info card for the left sidebar — matches the GCU layout. */
export default function PortalSidebar({ user, courses = [] }) {
  const [counselors, setCounselors] = useState([]);
  useEffect(() => {
    let active = true;
    api.get('/api/academy/my/counselors')
      .then(({ data }) => { if (active) setCounselors(data.rows || []); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  return (
    <aside className="w-full lg:w-[280px] shrink-0 space-y-4">
      {/* Brand promo */}
      <div
        className="rounded-2xl overflow-hidden border border-[#c9a227]/30"
        style={{
          background: 'linear-gradient(135deg,#fef3c7 0%,#fde68a 50%,#f5a623 100%)',
        }}
      >
        <div className="aspect-[4/3] flex items-center justify-center text-[#0e0e10]">
          <div className="text-center px-4">
            <div className="text-[10px] font-bold tracking-[0.3em] uppercase opacity-60">ZHELTA</div>
            <div className="mt-1 text-[26px] font-extrabold leading-tight">Career-ready<br />from day one</div>
          </div>
        </div>
        <div className="bg-white p-4 text-center">
          <p className="text-[12px] text-[#6E6E73]">Mentor-led, practical curriculum.</p>
        </div>
      </div>

      {/* My Courses */}
      <div className="rounded-xl overflow-hidden border border-[#E5E5E7] bg-white">
        <div className="bg-[#0e0e10] text-white px-4 py-2.5 text-[13px] font-bold tracking-[0.18em] uppercase">
          My Courses
        </div>
        <div className="p-3">
          {courses.length === 0 ? (
            <div className="text-[12px] text-[#6E6E73] px-2 py-3">No active courses yet.</div>
          ) : (
            <ul className="space-y-2">
              {courses.slice(0, 5).map((c) => (
                <li key={c.program_slug}>
                  <Link
                    to={`/academy/courses/${c.program_slug}`}
                    className="block px-3 py-2 rounded-lg hover:bg-[#fef8e7] text-[13px] font-semibold text-[#1D1D1F]"
                  >
                    {c.program_name}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* My Counselors */}
      {counselors.length > 0 && (
        <div className="rounded-xl overflow-hidden border border-[#E5E5E7] bg-white"
          data-testid="sidebar-counselors">
          <div className="bg-[#0e0e10] text-white px-4 py-2.5 text-[13px] font-bold tracking-[0.18em] uppercase">
            My Counselors
          </div>
          <ul className="p-3 space-y-3">
            {counselors.map((c) => (
              <li key={c.id || c.email} data-testid={`sidebar-counselor-${c.id || c.email}`}
                className="rounded-lg border border-[#E5E5E7] p-3">
                <div className="flex items-start gap-2.5">
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-[12px] font-extrabold text-[#0e0e10] tracking-wider"
                    style={{ background: 'linear-gradient(135deg,#fff8e7 0%,#ffe9a3 100%)' }}>
                    {initials(c.name)}
                  </span>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#a8871a] truncate">{c.role}</div>
                    <div className="text-[12.5px] font-extrabold text-[#0e0e10] leading-tight truncate">{c.name}</div>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {c.email && (
                    <a href={`mailto:${c.email}`}
                      data-testid={`sidebar-counselor-email-${c.id || c.email}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#E5E5E7] text-[11px] font-bold text-[#0e0e10] hover:border-[#c9a227] hover:bg-[#fff8e7]">
                      <Mail className="w-3 h-3 text-[#c9a227]" /> Email
                    </a>
                  )}
                  {c.phone && (
                    <a href={`tel:${c.phone.replace(/[^+\d]/g, '')}`}
                      data-testid={`sidebar-counselor-call-${c.id || c.email}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#E5E5E7] text-[11px] font-bold text-[#0e0e10] hover:border-[#c9a227] hover:bg-[#fff8e7]">
                      <Phone className="w-3 h-3 text-[#c9a227]" /> Call
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* My Information */}
      <div className="rounded-xl overflow-hidden border border-[#E5E5E7] bg-white">
        <div className="bg-[#0e0e10] text-white px-4 py-2.5 text-[13px] font-bold tracking-[0.18em] uppercase">
          My Information
        </div>
        <div className="p-4 space-y-1.5 text-[13px]">
          <div className="font-bold text-[#1D1D1F]">{user?.name || '—'}</div>
          <div className="text-[#6E6E73]">{user?.email}</div>
          <div className="text-[11px] uppercase tracking-wider text-[#c9a227] font-bold mt-2">{user?.role}</div>
          {user?.phone && <div className="text-[#6E6E73]">{user.phone}</div>}
        </div>
      </div>
    </aside>
  );
}
